import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  CssBaseline,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Divider,
  alpha,
  Badge,
  useTheme,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  CloudUpload as UploadIcon,
  RateReview as ReviewIcon,
  Warning as ExceptionIcon,
  Receipt as ReceiptIcon,
  LocalShipping as VehicleIcon,
  Inventory as PalletIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Language as LanguageIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { ThemeProvider, useThemeMode } from './context/ThemeContext';
import { LanguageProvider, useLanguage } from './i18n';
import { AuthProvider, useAuth } from './context/AuthContext';
import LandingPage from './components/LandingPage';
import LoginPage from './components/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import Footer from './components/Footer';
import Dashboard from './components/Dashboard';
import DocumentUpload from './components/DocumentUpload';
import DocumentReview from './components/DocumentReview';
import ExceptionQueue from './components/ExceptionQueue';
import UnloadingReceiptUpload from './components/Process2/UnloadingReceiptUpload';
import PalletVoucherUpload from './components/Process3/PalletVoucherUpload';
import VehicleUpload from './components/Process4/VehicleUpload';

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_COLLAPSED = 80;

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  badge?: number;
  collapsed: boolean;
}

const NavItem: React.FC<NavItemProps> = ({ to, icon, label, badge, collapsed }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Tooltip title={collapsed ? label : ''} placement="right" arrow>
      <ListItemButton
        component={Link}
        to={to}
        selected={isActive}
        sx={{
          minHeight: 38,
          justifyContent: collapsed ? 'center' : 'flex-start',
          px: collapsed ? 1.5 : 2,
          mx: 0.75,
          borderRadius: 1.5,
          mb: 0.25,
          transition: 'all 0.2s ease',
          color: 'rgba(255,255,255,0.6)',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.08)',
            color: 'rgba(255,255,255,0.9)',
          },
          '&.Mui-selected': {
            background: 'linear-gradient(135deg, rgba(139,92,246,0.2) 0%, rgba(59,130,246,0.12) 100%)',
            color: '#A78BFA',
            '& .MuiListItemIcon-root': {
              color: '#A78BFA',
            },
            '&:hover': {
              background: 'linear-gradient(135deg, rgba(139,92,246,0.25) 0%, rgba(59,130,246,0.15) 100%)',
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: collapsed ? 0 : 1.5,
            justifyContent: 'center',
            color: isActive ? '#A78BFA' : 'rgba(255,255,255,0.45)',
            '& .MuiSvgIcon-root': { fontSize: 20 },
          }}
        >
          {badge ? (
            <Badge badgeContent={badge} color="error" max={99} sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}>
              {icon}
            </Badge>
          ) : (
            icon
          )}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontSize: '0.8rem',
              fontWeight: isActive ? 600 : 450,
              letterSpacing: '0.01em',
            }}
          />
        )}
      </ListItemButton>
    </Tooltip>
  );
};

function MainLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const { language, setLanguage, t } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const drawerWidth = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  const handleLangMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleLanguageChange = (lang: 'en' | 'pt') => {
    setLanguage(lang);
    handleLangMenuClose();
  };

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      console.log('Searching for:', searchQuery);
      // Navigate to search results or filter documents
      alert(`Search functionality: "${searchQuery}"\n\nThis will search through all documents in the system.`);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            border: 'none',
            background: mode === 'dark'
              ? 'linear-gradient(180deg, #1a1a3e 0%, #0f0f2a 50%, #0a0a1f 100%)'
              : 'linear-gradient(180deg, #667eea 0%, #764ba2 50%, #6B8DD6 100%)',
            transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            overflowX: 'hidden',
            boxShadow: mode === 'dark' 
              ? '4px 0 20px rgba(139, 92, 246, 0.2)'
              : '4px 0 20px rgba(102, 126, 234, 0.3)',
          },
        }}
      >
        {/* Logo Section */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            minHeight: 72,
          }}
        >
          {!collapsed && (
            <Box 
              component="a"
              href="https://krelixir.com/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5, 
                textDecoration: 'none',
                '&:hover': { opacity: 0.9 },
              }}
            >
              <Box
                component="img"
                src="/image.png"
                alt="KrElixir Technology"
                sx={{
                  height: 45,
                  width: 'auto',
                  filter: 'brightness(0) invert(1)',
                }}
              />
            </Box>
          )}
          {collapsed && (
            <Box
              component="a"
              href="https://krelixir.com/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #2E4A7D 0%, #1E3A5F 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(46, 74, 125, 0.5)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                textDecoration: 'none',
                '&:hover': { opacity: 0.9 },
              }}
            >
              <Typography sx={{ color: 'white', fontWeight: 900, fontSize: '0.85rem' }}>
                KR
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ mx: 2, borderColor: 'rgba(255,255,255,0.08)' }} />

        {/* Navigation - scrollable */}
        <Box sx={{ flex: 1, py: 1.5, overflowY: 'auto', overflowX: 'hidden' }}>
          {!collapsed && (
            <Typography
              variant="overline"
              sx={{ px: 2.5, color: 'rgba(255,255,255,0.35)', fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.1em' }}
            >
              {t.nav.mainMenu}
            </Typography>
          )}
          <List sx={{ px: 1, pt: 0.5 }}>
            <NavItem to="/app" icon={<DashboardIcon />} label={t.nav.dashboard} collapsed={collapsed} />
          </List>

          {!collapsed && (
            <Typography
              variant="overline"
              sx={{ px: 2.5, mt: 1.5, display: 'block', color: 'rgba(255,255,255,0.35)', fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.1em' }}
            >
              {t.nav.processingSteps}
            </Typography>
          )}
          <List sx={{ px: 1, pt: 0.5 }}>
            <NavItem to="/app/upload" icon={<UploadIcon />} label={t.nav.step1} collapsed={collapsed} />
            <NavItem to="/app/receipts" icon={<ReceiptIcon />} label={t.nav.step2} collapsed={collapsed} />
            <NavItem to="/app/pallets" icon={<PalletIcon />} label={t.nav.step3} collapsed={collapsed} />
            <NavItem to="/app/vehicles" icon={<VehicleIcon />} label={t.nav.step4} collapsed={collapsed} />
          </List>

          {!collapsed && (
            <Typography
              variant="overline"
              sx={{ px: 2.5, mt: 1.5, display: 'block', color: 'rgba(255,255,255,0.35)', fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.1em' }}
            >
              {t.nav.management}
            </Typography>
          )}
          <List sx={{ px: 1, pt: 0.5 }}>
            <NavItem to="/app/review" icon={<ReviewIcon />} label={t.nav.documentReview} collapsed={collapsed} />
            <NavItem to="/app/exceptions" icon={<ExceptionIcon />} label={t.nav.exceptions} badge={3} collapsed={collapsed} />
          </List>
        </Box>

        {/* Bottom section: User + Collapse */}
        <Divider sx={{ mx: 2, borderColor: 'rgba(255,255,255,0.06)' }} />

        {/* User Section */}
        <Box sx={{
          px: 1.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.25,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}>
          <Avatar sx={{
            width: 32, height: 32,
            background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
            fontSize: '0.75rem', fontWeight: 700, color: 'white',
          }}>
            {user?.displayName ? user.displayName.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
          {!collapsed && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, color: 'white', fontSize: '0.75rem' }} noWrap>
                {user?.displayName || user?.email || 'User'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem' }} noWrap>
                {t.common.administrator}
              </Typography>
            </Box>
          )}
          <Tooltip title={t.auth.logout} placement={collapsed ? 'right' : 'top'}>
            <IconButton onClick={handleLogout} size="small" sx={{
              color: 'rgba(255,255,255,0.4)',
              '&:hover': { color: '#ef4444', background: 'rgba(239,68,68,0.08)' },
            }}>
              <LogoutIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Collapse Button */}
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          {!collapsed && (
            <Box
              component="a" href="https://krelixir.com/" target="_blank" rel="noopener noreferrer"
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                py: 0.75, px: 2, mb: 1, borderRadius: 1.5, background: 'rgba(255,255,255,0.04)',
                textDecoration: 'none', transition: 'all 0.2s ease',
                '&:hover': { background: 'rgba(255,255,255,0.08)' },
              }}
            >
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 500, fontSize: '0.65rem' }}>
                🌐 krelixir.com
              </Typography>
            </Box>
          )}
          <IconButton
            onClick={() => setCollapsed(!collapsed)}
            sx={{
              width: '100%', borderRadius: 1.5,
              backgroundColor: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.5)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)', color: 'white' },
            }}
          >
            {collapsed ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
          </IconButton>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top Bar */}
        <Box
          sx={{
            height: 72,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              size="small"
              placeholder={t.nav.searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#8B5CF6', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 320,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: mode === 'dark' ? 'rgba(139, 92, 246, 0.08)' : 'rgba(139, 92, 246, 0.05)',
                  '& fieldset': {
                    borderColor: mode === 'dark' ? 'rgba(139, 92, 246, 0.3)' : 'rgba(139, 92, 246, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#8B5CF6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#8B5CF6',
                    borderWidth: 2,
                  },
                },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Language Selector */}
            <Tooltip title={t.language.select}>
              <IconButton 
                onClick={handleLangMenuOpen}
                sx={{ 
                  color: '#8B5CF6',
                  '&:hover': { backgroundColor: alpha('#8B5CF6', 0.1) },
                }}
              >
                <LanguageIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={langAnchorEl}
              open={Boolean(langAnchorEl)}
              onClose={handleLangMenuClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 150,
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                },
              }}
            >
              <MenuItem 
                onClick={() => handleLanguageChange('en')}
                selected={language === 'en'}
                sx={{ 
                  gap: 1,
                  '&.Mui-selected': { backgroundColor: alpha('#8B5CF6', 0.1) },
                }}
              >
                <Box component="span" sx={{ fontSize: '1.2rem' }}>🇺🇸</Box>
                {t.language.english}
              </MenuItem>
              <MenuItem 
                onClick={() => handleLanguageChange('pt')}
                selected={language === 'pt'}
                sx={{ 
                  gap: 1,
                  '&.Mui-selected': { backgroundColor: alpha('#8B5CF6', 0.1) },
                }}
              >
                <Box component="span" sx={{ fontSize: '1.2rem' }}>🇧🇷</Box>
                {t.language.portuguese}
              </MenuItem>
            </Menu>

            {/* Theme Toggle */}
            <Tooltip title={mode === 'dark' ? t.theme.light : t.theme.dark}>
              <IconButton onClick={toggleTheme} sx={{ color: '#8B5CF6' }}>
                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={t.nav.notifications}>
              <IconButton>
                <Badge badgeContent={5} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title={t.nav.settings}>
              <IconButton>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Page Content */}
        <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1 }}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/upload" element={<DocumentUpload />} />
              <Route path="/review" element={<DocumentReview />} />
              <Route path="/exceptions" element={<ExceptionQueue />} />
              <Route path="/receipts" element={<UnloadingReceiptUpload />} />
              <Route path="/pallets" element={<PalletVoucherUpload />} />
              <Route path="/vehicles" element={<VehicleUpload />} />
            </Routes>
          </Box>
          <Footer />
        </Box>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <CssBaseline />
          <Router>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              {/* Protected App Routes */}
              <Route
                path="/app/*"
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
